/**
 * Transition System Types
 *
 * Clean, minimal type definitions for the transition system.
 * No legacy cruft, no optional fields that cause confusion.
 */

/**
 * Supported transition types (10 core transitions).
 * Designed for extensibility - add new types here as needed.
 */
export type TransitionType =
  // Fades (6)
  | 'crossfade'
  | 'dissolve'
  | 'fade-black'
  | 'fade-white'
  | 'dip-black'
  | 'dip-white'
  // Slides (8)
  | 'slide-left'
  | 'slide-right'
  | 'slide-up'
  | 'slide-down'
  | 'push-left'
  | 'push-right'
  | 'push-up'
  | 'push-down'
  // Wipes (12)
  | 'wipe-left'
  | 'wipe-right'
  | 'wipe-up'
  | 'wipe-down'
  | 'wipe-clock'
  | 'wipe-counter-clock'
  | 'wipe-radial'
  | 'wipe-iris'
  | 'wipe-diagonal-tl'
  | 'wipe-diagonal-tr'
  | 'wipe-diagonal-bl'
  | 'wipe-diagonal-br'
  // Zooms (4)
  | 'zoom-in'
  | 'zoom-out'
  | 'zoom-rotate-in'
  | 'zoom-rotate-out'
  // Spin (2)
  | 'spin-in'
  | 'spin-out'
  // Morph (4)
  | 'morph-circle'
  | 'morph-diamond'
  | 'morph-heart'
  | 'morph-star';

/**
 * Computed transition effect to apply to a node.
 * All values are absolute (not deltas).
 */
export type TransitionEffect = {
  /** Opacity from 0 to 1 */
  opacity: number;
  /** X offset in pixels (added to base position) */
  offsetX: number;
  /** Y offset in pixels (added to base position) */
  offsetY: number;
  /** Scale multiplier (1 = normal size) */
  scale: number;
  /** Rotation in degrees (added to base rotation) */
  rotation: number;
  /** Clip path for wipe transitions (null = no clipping) */
  clipPath: ((ctx: CanvasRenderingContext2D) => void) | null;
};

/**
 * A transition between two touching clips.
 * Transitions can ONLY exist between clips where clipA.end === clipB.start.
 */
export type Transition = {
  /** Unique identifier */
  id: string;
  /** ID of the outgoing clip (the one ending) */
  fromClipId: string;
  /** ID of the incoming clip (the one starting) */
  toClipId: string;
  /** Type of transition effect (accepts any string for forward compatibility) */
  type: string;
  /** Duration in seconds */
  duration: number;
};

/**
 * Minimal clip info needed for transition calculations.
 * Keeps transition system decoupled from full Clip type.
 */
export type ClipTiming = {
  id: string;
  start: number;
  end: number;
  elementId: string;
};

/**
 * Default effect (no transition active).
 */
export const DEFAULT_EFFECT: TransitionEffect = {
  opacity: 1,
  offsetX: 0,
  offsetY: 0,
  scale: 1,
  rotation: 0,
  clipPath: null,
};
